import React from "react";
import { Loader2, UserPlus } from "lucide-react";
import { Button } from "@shadcn-in-nx/ui/button";
import { Checkbox } from "@shadcn-in-nx/ui/checkbox";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

function PublishIntranetTab({
    hasTeamMembers, publicationInfo, isLoadingPublicationInfo,
    companyGroups, selectedTargets, setSelectedTargets,
    isPublishing, onPublish, onUnpublish, onClose,
}) {
    const { t } = useTranslation();
    const navigate = useNavigate();

    if (isLoadingPublicationInfo) {
        return (
            <div className="flex items-center justify-center py-12">
                <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
            </div>
        );
    }

    if (!hasTeamMembers) {
        return (
            <div className="py-6 space-y-4 text-center">
                <div className="mx-auto w-10 h-10 rounded-full bg-emerald-50 dark:bg-emerald-950/30 flex items-center justify-center">
                    <UserPlus className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
                </div>
                <div>
                    <p className="font-medium text-sm">{t("publishModal.groupPublishing.noTeamMembers.title")}</p>
                    <p className="text-xs text-muted-foreground mt-1 max-w-[280px] mx-auto">
                        {t("publishModal.groupPublishing.noTeamMembers.description")}
                    </p>
                </div>
                <Button size="sm" onClick={() => { onClose(); navigate("/profile/users"); }}>
                    <UserPlus className="w-3.5 h-3.5 mr-1.5" />
                    {t("publishModal.groupPublishing.noTeamMembers.addButton")}
                </Button>
            </div>
        );
    }

    const isPublished = publicationInfo?.isPublished;
    const toggleAll = (checked) => setSelectedTargets(checked ? ["all"] : []);
    const toggleGroup = (groupId, checked) => {
        if (checked) {
            setSelectedTargets([...selectedTargets.filter((id) => id !== "all"), groupId]);
        } else {
            setSelectedTargets(selectedTargets.filter((id) => id !== groupId));
        }
    };

    return (
        <div className="space-y-5">
            <p className="text-xs text-muted-foreground leading-relaxed">
                {t("publishModal.groupPublishing.description")}
            </p>

            {isPublished && (
                <div className="flex items-center gap-2 py-1">
                    <span className="relative flex h-2 w-2">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                        <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
                    </span>
                    <span className="text-xs font-medium text-emerald-700 dark:text-emerald-400">
                        {t("publishModal.groupPublishing.alreadyPublished")}
                    </span>
                </div>
            )}

            <div className="space-y-2.5">
                <label className="flex items-center gap-2.5 cursor-pointer">
                    <Checkbox checked={selectedTargets.includes("all")} onCheckedChange={toggleAll} />
                    <span className="text-sm font-medium">{t("publishModal.groupPublishing.publishToAllTeam")}</span>
                </label>

                {!selectedTargets.includes("all") && companyGroups.length > 0 && (
                    <div className="ml-7 space-y-2 pt-1">
                        <p className="text-xs text-muted-foreground">{t("publishModal.groupPublishing.selectSpecificGroups")}</p>
                        {companyGroups.map((group) => (
                            <label key={group.id} className="flex items-center gap-2.5 cursor-pointer">
                                <Checkbox checked={selectedTargets.includes(group.id)} onCheckedChange={(c) => toggleGroup(group.id, c)} />
                                <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: group.color || "#94a3b8" }} />
                                <span className="text-sm">{group.name}</span>
                                <span className="text-xs text-muted-foreground">({group.memberCount ?? group.members?.length ?? 0})</span>
                            </label>
                        ))}
                    </div>
                )}
            </div>

            <Button
                className="w-full"
                onClick={isPublished ? onUnpublish : onPublish}
                disabled={isPublishing || (!isPublished && selectedTargets.length === 0)}
                variant={isPublished ? "outline" : "default"}
            >
                {isPublishing && <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" />}
                {isPublished
                    ? t("publishModal.groupPublishing.unpublish")
                    : selectedTargets.includes("all")
                        ? t("publishModal.groupPublishing.publishToTeam")
                        : t("publishModal.groupPublishing.publishToGroups")}
            </Button>
        </div>
    );
}

export default React.memo(PublishIntranetTab);
